import { useEmailsManagedBy, useSetRoute } from '@features/app-hooks'
import { useOrganization } from '@features/organizations'
import { STAppHeaderNav } from '@features/st-organizations'
import { selOrganizationUsers } from '@features/st-organizations/st-organization-module'
import { FolderListRow, OrganizationRole } from '@st/sdk'
import { Button, Dialog, DialogButtons, LinkIcon, Modal, PlusIcon, Spinner } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { isEmpty, isNotEmpty } from '@st/util/json-value'
import { formatISO8601Date } from '@st/util/time'
import { ReactNode } from 'react'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { formatSchedule } from '@features/client-organizer-reminders/formatters'
import { STEnrollInRemindersPage } from '../client-organizer-reminders/st-enroll-in-reminders-page'
import { EditFolderTagsDialog } from '../st-folder-viewer/edit-folder-tags-dialog'
import {
  openSendClientOrganizerDialog,
  openShareableLinkDialog
} from '../st-folder-viewer/st-send-client-organizer-module'
import { STQuestionnairesBulkEmailPage } from '../st-questionnaire-send/st-questionnaires-bulk-email-page'
import { FolderListCommandPallette } from './folder-list-commands'
import { STFolderListFiltersBar } from './st-folder-list-filters-bar'
import {
  selAvailableBulkActions,
  selFinalFolderList,
  selFoldersById,
  selIsArchivingFolder,
  selIsBulkArchiving,
  selIsBulkDeleting,
  selIsBulkUnarchiving,
  selIsBulkUnenrolling,
  selIsDeletingFolder,
  STFolderAction
} from './st-folder-list-module'
import { STFolderListTable } from './st-folder-list-table'
import { STBulkAssignFoldersDialog } from './st-bulk-assign-folders-dialog'
import { STBulkAddChecklistItemsDialog } from './st-bulk-add-checklist-items-dialog'
import { STFolderListBatchActions } from './st-folder-list-batch-actions'
import { STFolderListExportCsvPage } from './st-folder-list-export-csv-page'
import { useOrganizationRole } from '../st-organizations/use-organization-role'
import { OrganizationLowBalanceFolderOpenDialog } from '../billing/organization-low-balance-folder-open-dialog'
import { OrganizationLowBalanceBanner } from '../billing/organization-low-balance-banner'
import { selLowBalanceModel } from '../billing/organization-billing-module'

export function STFolderListPage() {
  const organization = useOrganization()
  const role = useOrganizationRole()

  const emailsManagedBy = useEmailsManagedBy()
  const setRoute = useSetRoute()
  const stSendClientOrganizer = useProcess('stSendClientOrganizer')

  // `stFolderList` is booted at app startup; `organizationModule` wires organizationId via messages
  // when an org is loaded. Avoid init-arg wiring because the process may already exist.
  const stFolderList = useProcess('stFolderList')

  const state = useSelect('stFolderList', (s) => s)
  const availableBulkActions = useSelect('stFolderList', selAvailableBulkActions)
  const organizationUsers = useSelect('stOrganization', selOrganizationUsers)
  const lowBalance = useSelect('organizationBilling', selLowBalanceModel)
  const lowBalanceBanner = <OrganizationLowBalanceBanner role={role} />

  const dialog = useSelect('stFolderList', (s) => s?.dialog)
  const action = dialog?.action

  function closeRouteWorkflow() {
    stFolderList.send({ type: 'closeRouteWorkflow' })
  }

  function openEnrollInRemindersDialog(folderIds: string[]) {
    stFolderList.send({
      type: 'openDialog',
      action: { type: 'enrollInReminders', group: 'clientOrganizer', folderIds }
    })
  }

  function onAction(action: STFolderAction) {
    switch (action.type) {
      case 'openSendClientOrganizerDialog':
        stSendClientOrganizer.send(
          openSendClientOrganizerDialog({
            users: organizationUsers,
            folderId: action.folder.id,
            type: 'clientOrganizerEmail',
            messageType: 'client_organizer_invitation'
          })
        )
        break
      case 'openCopyShareableLinkDialog':
        stSendClientOrganizer.send(openShareableLinkDialog(action.folder.id, organizationUsers))
        break
      case 'openFolder':
        stFolderList.send({
          type: 'openFolder',
          folderId: action.folder.id,
          lowBalanceCurrentBalance: lowBalance?.hardBlock ? lowBalance.currentBalance : undefined
        })
        break
      case 'previewQuestionnaire':
        stFolderList.send({
          type: 'previewQuestionnaire',
          folderId: action.folderId
        })
        break
      case 'sendClientOrganizers': {
        stFolderList.send({ type: 'openDialog', action })
        break
      }
      case 'exportCsv':
        stFolderList.send({ type: 'openDialog', action })
        break
      case 'enrollInReminders':
        openEnrollInRemindersDialog(action.folderIds)
        break
      case 'unarchiveFolder':
        stFolderList.send({ type: 'unarchiveFolder', folder: action.folder })
        break
      case 'unlockClientOrganizer':
        stFolderList.send({ type: 'unlockClientOrganizer', folder: action.folder })
        break
      case 'submitClientOrganizer':
        stFolderList.send({
          type: 'submitClientOrganizer',
          folder: action.folder,
          forceSubmit: true
        })
        break
      case 'assignFolder':
        stFolderList.send({
          type: 'assignFolder',
          folderId: action.folder.id,
          userId: action.userId
        })
        break
      case 'unassignFolder':
        stFolderList.send({
          type: 'unassignFolder',
          folderId: action.folder.id,
          userId: action.userId
        })
        break
      default:
        stFolderList.send({ type: 'openDialog', action })
    }
  }

  if (!state) {
    return <FolderListLoadingState header={<STAppHeaderNav />} banner={lowBalanceBanner} />
  }

  const filters = (
    <STFolderListFiltersBar
      filters={state.filters}
      send={stFolderList.send}
      organizationUsers={organizationUsers}
    />
  )
  const buttons =
    role != 'restricted' ? (
      <div className="ml-auto grid auto-cols-max grid-flow-col items-center gap-2">
        <Button
          variant="default"
          leadingIcon={<LinkIcon className="h-4 w-4" />}
          onClick={() => setRoute({ name: 'magic_links' })}
        >
          Magic links
        </Button>
        <Button
          aria-label="Generate binders"
          variant="default"
          leadingIcon={<PlusIcon className="h-4 w-4" />}
          onClick={() =>
            stFolderList.send({
              type: 'openCreateImport'
            })
          }
        >
          Binders
        </Button>
      </div>
    ) : undefined

  switch (state.status) {
    case 'idle':
      return <FolderListLoadingState header={<STAppHeaderNav />} banner={lowBalanceBanner} />
    case 'loadingOrganization':
      return <FolderListLoadingState header={<STAppHeaderNav />} banner={lowBalanceBanner} />
    case 'failed':
      return (
        <FolderListScaffold title="Binders" header={<STAppHeaderNav />} banner={lowBalanceBanner}>
          <div role="alert" className="p-8 text-center text-gray-600">
            Failed to load binders. Refresh the page to try again.
          </div>
        </FolderListScaffold>
      )
    case 'loading':
      return (
        <FolderListLoadingState
          header={<STAppHeaderNav />}
          banner={lowBalanceBanner}
          filters={filters}
        />
      )
    case 'loaded':
      const routeWorkflow = state.routeWorkflow.workflow

      // Route workflows replace the list surface; local dialogs do not.
      if (routeWorkflow.type == 'sendClientOrganizers') {
        return (
          <STQuestionnairesBulkEmailPage
            state={routeWorkflow.send}
            allFolders={Object.values(state.folderListState.folders)}
            send={(message) => stFolderList.send({ type: 'bulkSendClientOrganizers', message })}
            emailsManagedBy={emailsManagedBy}
            onClose={closeRouteWorkflow}
            onClickEnrollInReminders={openEnrollInRemindersDialog}
          />
        )
      }
      if (routeWorkflow.type == 'enrollInReminders') {
        return (
          <STEnrollInRemindersPage
            state={routeWorkflow.enroll}
            folders={match(routeWorkflow.enroll)
              .with({ status: 'idle' }, () => [])
              .otherwise((enroll) => selFoldersById(state, enroll.folderIds))}
            send={(message) => stFolderList.send({ type: 'bulkEnrollInReminders', message })}
            emailsManagedBy={emailsManagedBy}
            onClose={closeRouteWorkflow}
          />
        )
      }
      if (action?.type == 'exportCsv') {
        return (
          <STFolderListExportCsvPage
            exportState={state.csvExport}
            send={stFolderList.send}
            emailsManagedBy={emailsManagedBy}
            onClose={() => stFolderList.send({ type: 'closeDialog' })}
            onClickBack={() => stFolderList.send({ type: 'closeDialog' })}
          />
        )
      }

      if (isEmpty(state.folderListState.folders)) {
        return (
          <FolderListScaffold title="Binders" header={<STAppHeaderNav />} banner={lowBalanceBanner}>
            <FolderListEmptyState
              role={role}
              onAddClients={() =>
                stFolderList.send({
                  type: 'openCreateImport'
                })
              }
            />
          </FolderListScaffold>
        )
      }

      const filteredFolders = selFinalFolderList(state, organizationUsers)

      return (
        <FolderListScaffold
          header={<STAppHeaderNav />}
          banner={lowBalanceBanner}
          filters={filters}
          buttons={buttons}
          batchActions={
            state.selectedFolderIds.length > 0 ? (
              <STFolderListBatchActions
                availableBulkActions={availableBulkActions}
                selectedFolderIds={state.selectedFolderIds}
                onAction={onAction}
                onClearSelection={() => stFolderList.send({ type: 'deselectAll' })}
              />
            ) : undefined
          }
        >
          <STFolderListTable
            items={filteredFolders}
            organizationUsers={organizationUsers}
            selectedFolderIds={state.selectedFolderIds}
            sortState={state.sortState}
            // Scope scroll memory to the organization so table position never leaks across orgs.
            scrollMemoryKey={`folder-list:${organization.id}`}
            send={stFolderList.send}
            onAction={onAction}
            onToggleSort={(column) => stFolderList.send({ type: 'toggleSort', column })}
          />

          {match(action)
            .with({ type: 'openFolderBlockedByLowBalance' }, ({ currentBalance }) => {
              return (
                <Modal isOpen={true} onClose={() => stFolderList.send({ type: 'closeDialog' })}>
                  <OrganizationLowBalanceFolderOpenDialog
                    currentBalance={currentBalance}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'deleteFolder' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <STDeleteFolderDialog
                    folder={folder}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'archiveFolder' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <STArchiveFolderDialog
                    folder={folder}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'editFolderTags' }, ({ folder }) => {
              return (
                <Modal isOpen={true}>
                  <EditFolderTagsDialog
                    folderId={folder.id}
                    tagIds={folder.tagIds}
                    folderName={folder.clientName}
                    onSave={({ folderId, addTagIds, removeTagIds }) =>
                      stFolderList.send({
                        type: 'updateFolderTags',
                        folderId,
                        addTagIds,
                        removeTagIds
                      })
                    }
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'archiveFolders' }, ({ folderIds }) => {
              return (
                <Modal isOpen={true}>
                  <STConfirmArchiveFoldersDialog
                    folderIds={folderIds}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'unarchiveFolders' }, ({ folderIds }) => {
              return (
                <Modal isOpen={true}>
                  <STConfirmUnarchiveFoldersDialog
                    folderIds={folderIds}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'deleteFolders' }, ({ folderIds }) => {
              return (
                <Modal isOpen={true}>
                  <STConfirmDeleteFoldersDialog
                    folderIds={folderIds}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'assignFolders' }, ({ folderIds }) => {
              const selectedFolders = folderIds
                .map((id) => state.folderListState.folders[id])
                .filter(isNotEmpty)
              return (
                <Modal isOpen={true}>
                  <STBulkAssignFoldersDialog
                    folderIds={folderIds}
                    folders={selectedFolders}
                    organizationUsers={organizationUsers}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'addChecklistItems' }, ({ folderIds }) => {
              const selectedFolders: FolderListRow[] = folderIds
                .map((id) => state.folderListState.folders[id])
                .filter(isNotEmpty)
              return (
                <Modal isOpen={true}>
                  <STBulkAddChecklistItemsDialog
                    folders={selectedFolders}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                  />
                </Modal>
              )
            })
            .with({ type: 'unenrollFromReminders' }, ({ folderIds }) => {
              return (
                <Modal isOpen={true}>
                  <STConfirmUnenrollFromRemindersDialog
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                    folderIds={folderIds}
                  />
                </Modal>
              )
            })
            .with({ type: 'viewRemindersSchedule' }, ({ folder, reminderSchedule }) => {
              return (
                <Modal isOpen={true}>
                  <Dialog
                    title="Automated Reminder"
                    subtitle={`A reminder will be sent to ${folder.clientName} ${formatSchedule(
                      reminderSchedule
                    )} starting on ${formatISO8601Date(
                      reminderSchedule.firstDate
                    )} and ending on ${formatISO8601Date(
                      reminderSchedule.lastDate
                    )} until they submit their questionnaire. You can view or modify the reminder email template in your Settings.`}
                    buttons={
                      <DialogButtons>
                        <Button
                          variant="primary"
                          onClick={() => stFolderList.send({ type: 'closeDialog' })}
                        >
                          OK
                        </Button>
                      </DialogButtons>
                    }
                  />
                </Modal>
              )
            })
            .with({ type: 'openCommandPalette' }, () => {
              return (
                <Modal isOpen={true} onClose={() => stFolderList.send({ type: 'closeDialog' })}>
                  <FolderListCommandPallette
                    organization={organization}
                    onClose={() => stFolderList.send({ type: 'closeDialog' })}
                    onCopyOrganizationId={() => stFolderList.send({ type: 'copyOrganizationId' })}
                  />
                </Modal>
              )
            })
            .otherwise(() => null)}
        </FolderListScaffold>
      )
  }
}
function FolderListScaffold({
  title,
  banner,
  filters,
  header,
  children,
  buttons,
  batchActions
}: {
  title?: string
  filters?: ReactNode
  banner?: ReactNode
  header: ReactNode
  children: ReactNode
  buttons?: ReactNode
  batchActions?: ReactNode
}) {
  return (
    <div className={'grid h-dvh min-w-[1200px] grid-rows-[auto_auto_minmax(0,1fr)_auto]'}>
      {header}

      <div className="flex flex-col">
        {banner}
        <div className="flex h-16 flex-row items-center px-20 py-12">
          {title && <h1 className="mr-6 text-2xl">{title}</h1>}
          {filters ?? <div />}
          {buttons ?? <div />}
        </div>
      </div>

      {children}

      {batchActions ? <div className="relative z-20">{batchActions}</div> : null}
    </div>
  )
}

function FolderListLoadingState({
  header,
  banner,
  filters
}: {
  header: ReactNode
  banner?: ReactNode
  filters?: ReactNode
}) {
  return (
    <FolderListScaffold header={header} banner={banner} filters={filters}>
      <div className="min-h-0 overflow-hidden bg-white">
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading binders"
          className="flex h-full min-h-[320px] items-center justify-center"
        >
          <Spinner className="size-8 fill-blue-500 text-blue-100" />
          <span className="sr-only">Loading binders</span>
        </div>
      </div>
    </FolderListScaffold>
  )
}

function STConfirmArchiveFoldersDialog({
  onClose,
  folderIds
}: {
  onClose: () => void
  folderIds: string[]
}) {
  const folderList = useProcess('stFolderList')
  const isArchiving = useSelect('stFolderList', (s) => selIsBulkArchiving(s, folderIds))

  return (
    <Dialog
      title="Archive binders"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={isArchiving ? <Spinner className="size-4" /> : undefined}
            disabled={isArchiving}
            onClick={() => {
              folderList.send({
                type: 'bulkArchiveFolders',
                folderIds: folderIds
              })
              // it would be confusing if the archived folders stay selected because they will
              // be sweeped into a separate archived folder list and disappear from view
              folderList.send({ type: 'deselectAll' })
            }}
          >
            {isArchiving ? 'Archiving...' : 'Confirm archive'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Archiving this selection will make these binders read-only. Neither you nor your clients
        will be able to make changes. You can unarchive them at any time.
      </p>
    </Dialog>
  )
}

function STConfirmUnarchiveFoldersDialog({
  onClose,
  folderIds
}: {
  onClose: () => void
  folderIds: string[]
}) {
  const folderList = useProcess('stFolderList')
  const isUnarchiving = useSelect('stFolderList', (s) => selIsBulkUnarchiving(s, folderIds))

  return (
    <Dialog
      title="Unarchive binders"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={isUnarchiving ? <Spinner className="size-4" /> : undefined}
            disabled={isUnarchiving}
            onClick={() => {
              folderList.send({
                type: 'bulkUnarchiveFolders',
                folderIds: folderIds
              })
            }}
          >
            {isUnarchiving ? 'Unarchiving...' : 'Confirm unarchive'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Please confirm whether you would like to unarchive {folderIds.length}{' '}
        {inflect(folderIds.length, 'binder')}.
      </p>
    </Dialog>
  )
}

function STConfirmDeleteFoldersDialog({
  onClose,
  folderIds
}: {
  onClose: () => void
  folderIds: string[]
}) {
  const folderList = useProcess('stFolderList')
  const isDeleting = useSelect('stFolderList', (s) => selIsBulkDeleting(s, folderIds))

  return (
    <Dialog
      title="Delete binders"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={isDeleting ? <Spinner className="size-4" /> : undefined}
            disabled={isDeleting}
            onClick={() => {
              folderList.send({ type: 'bulkDeleteFolders', folderIds: folderIds })
            }}
          >
            {isDeleting ? 'Deleting...' : 'Confirm delete'}
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-2">
        <p>
          Please confirm whether you would like to delete {folderIds.length}{' '}
          {inflect(folderIds.length, 'binder')}.
        </p>
        <p className="text-red-600">
          Warning: This action is irreversible. If you proceed, the binders will be deleted
          permanently. It is recommended to use archive in most cases.
        </p>
      </div>
    </Dialog>
  )
}

function STArchiveFolderDialog({
  folder,
  onClose
}: {
  folder: FolderListRow
  onClose: () => void
}) {
  const folderList = useProcess('stFolderList')
  const isArchiving = useSelect('stFolderList', (s) => selIsArchivingFolder(s, folder.id))

  return (
    <Dialog
      title="Archive binder"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={isArchiving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isArchiving}
            leadingIcon={isArchiving ? <Spinner className="size-4" /> : undefined}
            onClick={() => {
              folderList.send({ type: 'archiveFolder', folder: folder })
            }}
          >
            {isArchiving ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Archiving this binder will make it read-only. Neither you nor your clients will be able to
        make changes. You can unarchive it at any time.
      </p>
    </Dialog>
  )
}

function STDeleteFolderDialog({ folder, onClose }: { folder: FolderListRow; onClose: () => void }) {
  const folderList = useProcess('stFolderList')
  const isDeleting = useSelect('stFolderList', (s) => selIsDeletingFolder(s, folder.id))

  return (
    <Dialog
      title="Delete binder"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isDeleting}
            leadingIcon={isDeleting ? <Spinner className="size-4" /> : undefined}
            onClick={() => {
              folderList.send({ type: 'deleteFolder', folder: folder })
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogButtons>
      }
    >
      <p>Please confirm whether you would like to delete the binder for {folder.name}.</p>
    </Dialog>
  )
}

function STConfirmUnenrollFromRemindersDialog({
  onClose,
  folderIds
}: {
  onClose: () => void
  folderIds: string[]
}) {
  const folderList = useProcess('stFolderList')
  const isUnenrolling = useSelect('stFolderList', (s) => selIsBulkUnenrolling(s, folderIds))

  return (
    <Dialog
      title="Unenroll from reminders"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={() => onClose()} disabled={isUnenrolling}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isUnenrolling}
            leadingIcon={isUnenrolling ? <Spinner className="size-4" /> : undefined}
            onClick={() => {
              folderList.send({
                type: 'bulkUnenrollFromReminders',
                folderIds: folderIds
              })
            }}
          >
            {isUnenrolling ? 'Unenrolling...' : 'Confirm unenroll'}
          </Button>
        </DialogButtons>
      }
    >
      <p>
        Please confirm whether you would like to unenroll {folderIds.length}{' '}
        {inflect(folderIds.length, 'client')} from automated reminders. You can re-enroll them at
        any time.
      </p>
    </Dialog>
  )
}

function FolderListEmptyState({
  role,
  onAddClients
}: {
  role: OrganizationRole
  onAddClients: () => void
}) {
  return (
    <div className="mx-auto mt-12 flex w-[650px] flex-col items-center gap-3">
      <div className="text-center text-xl text-gray-900">
        Generate your first StanfordTax Binder, directly from your tax software!
      </div>
      <div className="text-center text-base text-gray-700">
        Generate a binder for each return to keep track of client documents. Every binder comes with
        a client organizer which you can optionally send to clients. Information clients add to
        their organizer will automatically flow into the binder.
      </div>
      {role != 'restricted' && (
        <Button
          variant="primary"
          leadingIcon={<PlusIcon className="h-4 w-4" />}
          className="mt-4"
          onClick={onAddClients}
        >
          Generate binders
        </Button>
      )}
    </div>
  )
}
